import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import { useMobileNavigation } from '../hooks/useMobileNavigation';
import { useWatchlistManager } from '../hooks/useWatchlistManager';
import { useWatchlistStore, useUIStore } from '../stores';
import { auth } from '../lib/supabase';
import ErrorBoundary from './ErrorBoundary';
import LoadingSpinner from './LoadingSpinner';

// Lazy load components
const MobileWatchlistPanel = lazy(() => import('./mobile/MobileWatchlistPanel'));
const MobileMainContent = lazy(() => import('./mobile/MobileMainContent'));
const MobileHomePage = lazy(() => import('./mobile/MobileHomePage'));
const MobileBottomNav = lazy(() => import('./mobile/MobileBottomNav'));
const AuthModal = lazy(() => import('./AuthModal'));
const PWAInstallPrompt = lazy(() => import('./PWAInstallPrompt'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const WatchlistPanel = lazy(() => import('./WatchlistPanel'));
const MainContent = lazy(() => import('./MainContent'));

function AppContent() {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  
  // Store states
  const {
    selectedCompany,
    selectedIndustry,
    showIndustrySelector,
    setSelectedCompany,
    setSelectedIndustry,
    setShowIndustrySelector,
  } = useWatchlistStore();
  
  // Watchlist manager
  const {
    watchedCompanies,
    watchedIndustries,
    watchlistInitialized,
    refreshWatchlist,
  } = useWatchlistManager();
  
  const {
    showAuthModal,
    showPWAPrompt,
    isPWAInstalled,
    setShowAuthModal,
    setShowPWAPrompt,
    setIsPWAInstalled,
  } = useUIStore();
  
  // Mobile navigation
  const {
    showMobilePanel,
    mobileBottomNavTab,
    handleCompanySelect,
    handleIndustrySelect,
    handleMobileBottomNavChange,
    handleBackToList,
    handleLogoClick,
  } = useMobileNavigation(isMobile);
  
  // 重置密码页面检测
  const [isResetPage] = useState(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    
    const type = hashParams.get('type') || queryParams.get('type');
    const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
    
    return type === 'recovery' && accessToken && refreshToken;
  });

  // PWA安装状态检测
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsPWAInstalled(isStandalone);
  }, [setIsPWAInstalled]);

  // 处理退出登录
  const handleSignOut = async () => {
    try {
      await auth.signOut();
      setSelectedCompany(null);
      setShowIndustrySelector(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // 处理行业选择器相关操作
  const handleShowIndustrySelector = () => {
    setSelectedCompany(null);
    setSelectedIndustry(null);
    setShowIndustrySelector(true);
    
    if (isMobile) {
      // Mobile logic handled by store
    }
  };

  const handleCloseIndustrySelector = () => {
    setShowIndustrySelector(false);
  };

  const existingIndustries = watchedIndustries.map(i => i.name);

  // 如果是重置密码页面，直接显示重置密码组件
  if (isResetPage) {
    return (
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <ResetPassword />
      </Suspense>
    );
  }

  // 显示全屏加载器
  if (loading || (user && !watchlistInitialized)) {
    const loadingText = loading ? "应用启动中..." : "加载用户数据中...";
    return <LoadingSpinner fullScreen text={loadingText} />;
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Global error caught:', error, errorInfo);
      }}
    >
      <div className={`${isMobile ? 'min-h-screen pwa-container' : 'h-screen'} flex flex-col`} style={{ backgroundColor: '#faf7ff' }}>
        {/* Header */}
        <header className={`relative z-10 ${isMobile && !user ? 'sticky top-0' : ''}`}>
          {/* PWA状态栏填充区域 */}
          {isMobile && (
            <div className="pwa-safe-top" style={{ backgroundColor: 'rgba(250, 247, 255, 0.95)' }} />
          )}
          
          <div className="rounded-none border-0 border-b border-white/20" style={{ backgroundColor: 'rgba(250, 247, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <div className={`max-w-[1920px] mx-auto ${isMobile ? 'px-4 py-3' : 'px-8 py-6'}`}>
              <div className="flex items-center justify-between">
                {/* Logo */}
                <div className="flex items-center gap-6">
                  <h1
                    className={`font-bold text-gradient cursor-pointer hover:opacity-80 transition-all duration-300 hover:scale-105 ${isMobile ? 'text-xl' : 'text-3xl'}`}
                    onClick={handleLogoClick}
                  >
                    FOMO
                  </h1>
                </div>

                {/* User Actions */}
                <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-4'}`}>
                  {/* PWA Install Button */}
                  {isMobile && !isPWAInstalled && (
                    <button
                      onClick={() => setShowPWAPrompt(true)}
                      className="bg-brand-600 hover:bg-brand-700 text-white rounded-full font-medium transition-all duration-300 shadow-soft px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      安装
                    </button>
                  )}
                  
                  {/* Contact Developer Button */}
                  <div className="relative group">
                    <button className={`bg-white rounded-full font-medium text-gray-700 hover:bg-white transition-all duration-300 shadow-soft ${isMobile ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'}`}>
                      联系开发者
                    </button>
                    <div className={`absolute top-full mt-2 right-0 bg-gray-900 text-white rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-20 ${
                      isMobile ? 'text-xs px-3 py-2.5 max-w-[240px]' : 'text-sm px-3 py-2 whitespace-nowrap'
                    }`}>
                      {isMobile ? (
                        <div className="space-y-1">
                          <div>欢迎添加开发者微信</div>
                          <div className="font-semibold text-base">15611887515</div>
                          <div>提出你的需求和建议！</div>
                        </div>
                      ) : (
                        <>
                          欢迎添加开发者微信15611887515提出你的需求和建议！
                          <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                        </>
                      )}
                      {isMobile && (
                        <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      )}
                    </div>
                  </div>
                  
                  {user ? (
                    <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
                      {!isMobile && (
                        <div className="glass rounded-full px-4 py-2">
                          <span className="text-sm font-medium text-gray-700">欢迎，{user.email?.split('@')[0]}</span>
                        </div>
                      )}
                      <button
                        onClick={handleSignOut}
                        className={`btn-brand rounded-full ${isMobile ? 'text-xs px-3 py-1.5' : 'text-sm px-5 py-2'}`}
                      >
                        退出
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className={`btn-brand rounded-full ${isMobile ? 'text-xs px-4 py-1.5' : 'text-sm px-6 py-2'}`}
                    >
                      登录
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className={isMobile ? 'flex-1' : 'flex-1 min-h-0'}>
          <div className={`${isMobile ? 'w-full' : 'h-full max-w-[1920px] mx-auto px-4 md:px-8 pt-2 pb-0'}`}>
            {isMobile ? (
              // Mobile Layout
              <>
                <div className="pb-6">
                  {!user ? (
                    // Mobile未登录
                    <div className="px-4">
                      <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><LoadingSpinner /></div>}>
                        <MobileMainContent 
                          selectedCompany={null}
                          selectedIndustry={null}
                          showIndustrySelector={false}
                          onCloseIndustrySelector={handleCloseIndustrySelector}
                          refreshWatchlist={refreshWatchlist}
                          existingIndustries={existingIndustries}
                          watchedCompanies={watchedCompanies}
                          watchlistInitialized={watchlistInitialized}
                          onCompanySelectFromDashboard={handleCompanySelect}
                          onBackToList={() => {}}
                        />
                      </Suspense>
                    </div>
                  ) : (
                    // Mobile已登录
                    <>
                      {(selectedCompany || selectedIndustry || showIndustrySelector) && showMobilePanel === 'content' ? (
                        // 显示内容面板
                        <div className="px-4">
                          <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><LoadingSpinner text="加载内容中..." /></div>}>
                            <MobileMainContent 
                              selectedCompany={selectedCompany}
                              selectedIndustry={selectedIndustry}
                              showIndustrySelector={showIndustrySelector}
                              onCloseIndustrySelector={handleCloseIndustrySelector}
                              refreshWatchlist={refreshWatchlist}
                              existingIndustries={existingIndustries}
                              watchedCompanies={watchedCompanies}
                              watchlistInitialized={watchlistInitialized}
                              onCompanySelectFromDashboard={handleCompanySelect}
                              onBackToList={handleBackToList}
                            />
                          </Suspense>
                        </div>
                      ) : (
                        // 显示列表面板
                        <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><LoadingSpinner /></div>}>
                          {mobileBottomNavTab === 'home' ? (
                            <MobileHomePage 
                              user={user}
                              watchedCompanies={watchedCompanies}
                              onCompanySelect={handleCompanySelect}
                              watchlistInitialized={watchlistInitialized}
                            />
                          ) : (
                            <MobileWatchlistPanel 
                              onCompanySelect={handleCompanySelect}
                              onIndustrySelect={handleIndustrySelect}
                              onShowIndustrySelector={handleShowIndustrySelector}
                              onRefreshWatchlist={refreshWatchlist}
                              onIndustriesUpdate={() => {}}
                              onWatchedCompaniesUpdate={() => {}}
                              activeBottomTab={mobileBottomNavTab}
                              watchlistInitialized={watchlistInitialized}
                              watchedCompanies={watchedCompanies}
                              watchedIndustries={watchedIndustries}
                              refreshWatchlist={refreshWatchlist}
                            />
                          )}
                        </Suspense>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              // Desktop Layout
              <div className="grid grid-cols-[360px_1fr] gap-8 h-full">
                {/* Left Panel */}
                <div className="h-full min-h-0">
                  <Suspense fallback={<div className="card-modern h-full flex items-center justify-center"><LoadingSpinner /></div>}>
                    <WatchlistPanel 
                      onCompanySelect={handleCompanySelect}
                      onIndustrySelect={handleIndustrySelect}
                      onShowIndustrySelector={handleShowIndustrySelector}
                      onRefreshWatchlist={refreshWatchlist}
                      onIndustriesUpdate={() => {}}
                      onWatchedCompaniesUpdate={() => {}}
                      onWatchedIndustriesUpdate={() => {}}
                    />
                  </Suspense>
                </div>

                {/* Right Panel */}
                <div className="h-full min-h-0">
                  <Suspense fallback={<div className="card-modern h-full flex items-center justify-center"><LoadingSpinner text="加载内容中..." /></div>}>
                    <MainContent 
                      selectedCompany={selectedCompany}
                      selectedIndustry={selectedIndustry}
                      showIndustrySelector={showIndustrySelector}
                      onCloseIndustrySelector={handleCloseIndustrySelector}
                      refreshWatchlist={() => Promise.resolve()}
                      existingIndustries={existingIndustries}
                      watchedCompanies={watchedCompanies}
                      watchlistInitialized={watchlistInitialized}
                      onCompanySelectFromDashboard={handleCompanySelect}
                    />
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        {isMobile && user && (
          <Suspense fallback={null}>
            <MobileBottomNav 
              activeTab={mobileBottomNavTab}
              onTabChange={handleMobileBottomNavChange}
            />
          </Suspense>
        )}

        {/* Modals */}
        <Suspense fallback={null}>
          <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
        </Suspense>

        {isMobile && (
          <Suspense fallback={null}>
            <PWAInstallPrompt 
              onClose={() => setShowPWAPrompt(false)} 
              isManualTrigger={showPWAPrompt}
            />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default AppContent;